import { syntaxTree } from "@codemirror/language";
import { type EditorState, type Extension } from "@codemirror/state";
import { showTooltip, type Tooltip } from "@codemirror/view";
import katex from "katex";

/** Overleaf-style live math preview: while the caret is inside a formula, the
 *  rendered result floats just above it.
 *
 *  The live-preview layer already compiles `$x$` into a widget — but only once
 *  the caret LEAVES, because the source has to be visible to be edited. That
 *  leaves the one moment you most want to see the formula, while typing it, as
 *  the one moment you cannot. A tooltip resolves the conflict instead of
 *  trading one against the other: the source stays editable in place and the
 *  rendered form sits above it. */

export interface MathAtCursor {
  from: number;
  to: number;
  tex: string;
  /** `$$…$$` renders in display mode, like the compiled output. */
  display: boolean;
}

/** Positions of every `$$` in the document, in order. */
function blockDelimiters(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < text.length; i++) {
    if (text[i] === "$" && text[i + 1] === "$") {
      out.push(i);
      i++; // never let one `$$` be read as two overlapping delimiters
    }
  }
  return out;
}

/** The formula the cursor is inside, or null.
 *
 *  Block math is found textually rather than off the syntax tree: `$$…$$` is
 *  deliberately NOT a parsed node (it stays raw so Preview can render it), and
 *  it spans lines, which inline nodes never do. Pairing the delimiters in order
 *  means an unmatched trailing `$$` simply owns nothing. */
export function mathAtCursor(state: EditorState): MathAtCursor | null {
  const pos = state.selection.main.head;
  const doc = state.doc.toString();

  const marks = blockDelimiters(doc);
  for (let i = 0; i + 1 < marks.length; i += 2) {
    const open = marks[i]!;
    const close = marks[i + 1]!;
    if (pos < open || pos > close + 2) continue;
    const tex = doc.slice(open + 2, close);
    return tex.trim() ? { from: open, to: close + 2, tex, display: true } : null;
  }

  // Inline `$x$` — a real node, courtesy of `markdown-extensions.ts`. BOTH
  // sides are tried: at a node boundary (which is exactly where the caret lands
  // when you click a rendered formula to edit it) a one-sided lookup resolves
  // to the neighbour and finds no math at all.
  const tree = syntaxTree(state);
  for (const side of [-1, 1] as const) {
    for (let n = tree.resolveInner(pos, side); n; n = n.parent!) {
      if (n.name === "InlineMath") {
        const tex = state.sliceDoc(n.from + 1, n.to - 1);
        return tex.trim() ? { from: n.from, to: n.to, tex, display: false } : null;
      }
      if (!n.parent) break;
    }
  }
  return null;
}

/** Render `tex`, or the error KaTeX raises, into a preview element. Errors are
 *  shown rather than swallowed — a formula that will not compile is exactly
 *  what the preview is for. */
export function renderMathPreview(tex: string, display: boolean): HTMLElement {
  const dom = document.createElement("div");
  dom.className = "cm-math-preview";
  try {
    dom.innerHTML = katex.renderToString(tex, { displayMode: display, throwOnError: true });
  } catch (err) {
    dom.classList.add("cm-math-preview--error");
    dom.textContent = err instanceof Error ? err.message.replace(/^KaTeX parse error:\s*/, "") : String(err);
  }
  return dom;
}

function tooltipFor(state: EditorState): Tooltip | null {
  const hit = mathAtCursor(state);
  if (!hit) return null;
  return {
    // Anchored to the start of the CARET'S line, not the formula's. Anchoring
    // to the formula start put the card above the block's opening `$$`, which
    // for a multi-line equation is several lines from where you are typing —
    // it covered unrelated prose and left the cursor with nothing above it.
    // The line start also keeps the card from skating sideways as the
    // expression grows.
    pos: state.doc.lineAt(state.selection.main.head).from,
    above: true,
    // `strictSide: false` lets it flip below when the formula is on the first
    // visible line and there is no room above.
    strictSide: false,
    arrow: true,
    create: () => ({ dom: renderMathPreview(hit.tex, hit.display) }),
  };
}

/** The live math preview. */
export const mathPreview: Extension = showTooltip.compute(["doc", "selection"], tooltipFor);
